<template>
  <!--
    mousedown, not click: a drag that starts on a choice button and is released
    over the backdrop fires `click` on the backdrop and would throw the choice
    away. Modal.vue guards the same way, for the same reason.
  -->
  <div class="action-price-dialog" data-qa="action-price-dialog" @mousedown.self="$emit('close')">
    <div
      class="action-price-dialog__box"
      role="dialog"
      aria-modal="true"
      :aria-label="i18n.t('/AccessoryCatalog/PriceChoiceTitle')"
    >
      <h3>{{ i18n.t('/AccessoryCatalog/PriceChoiceTitle') }}</h3>
      <p class="action-price-dialog__intro">{{ intro }}</p>

      <div class="action-price-dialog__choices">
        <button
          ref="autoline"
          type="button"
          class="action-price-dialog__choice"
          data-qa="action-price-choose-autoline"
          @click="$emit('choose', PRICE_CHOICE_AUTOLINE)"
        >
          <span class="k">
            {{ i18n.t('/AccessoryCatalog/PriceChoiceAutolineLabel') }}
            <small>{{ i18n.t('/AccessoryCatalog/PriceChoiceAutolineSub') }}</small>
          </span>
          <span class="v">
            {{ i18n.n(autolinePrice, 'currency') }}
            <small>{{ priceSub }}</small>
          </span>
        </button>

        <!--
          No action price means there is no second option to offer. The container
          only opens this dialog when one differs from Autoline, so this is a
          guard rather than a state the user reaches — but offering a € 0,00
          "action price" that `withActionPrice` would then silently not apply is
          the wrong way to be defensive.
        -->
        <button
          v-if="action"
          type="button"
          class="action-price-dialog__choice action-price-dialog__choice--action"
          data-qa="action-price-choose-action"
          @click="$emit('choose', PRICE_CHOICE_ACTION)"
        >
          <span class="k">
            {{ i18n.t('/AccessoryCatalog/PriceChoiceActionLabel') }}
            <small>{{ validitySub }}</small>
          </span>
          <span class="v">
            {{ i18n.n(actionAtVat, 'currency') }}
            <small>{{ priceSub }}</small>
          </span>
        </button>
      </div>

      <div class="action-price-dialog__foot">
        <app-button
          button-class="action-quiet"
          data-qa="action-price-cancel"
          @click="$emit('close')"
        >
          {{ i18n.t('/AccessoryCatalog/PriceChoiceCancel') }}
        </app-button>
      </div>
    </div>
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import {
  PRICE_CHOICE_ACTION,
  PRICE_CHOICE_AUTOLINE,
  VAT_EXCL,
  VAT_INCL,
  actionPrice,
  hasLabour,
  toNumber,
} from '@/helpers/ponCatalog'
import { displayTitle } from '@/helpers/ponCatalogFilters'
import dateHelper from '@/helpers/dateHelper'
import modalFocus from '@/mixins/modalFocus'

export default {
  components: {
    AppButton,
  },
  mixins: [modalFocus],
  props: {
    /** Catalog accessory with an active action price that differs from Autoline */
    accessory: {
      type: Object,
      required: true,
    },
    /** Which price the two choices show: VAT_INCL or VAT_EXCL */
    vat: {
      type: String,
      default: VAT_INCL,
    },
    /**
     * The catalog's shared clock, so an action that expires while the dialog is
     * open does not change what this dialog offers halfway through the choice.
     */
    now: {
      type: Date,
      default: null,
    },
  },
  emits: ['choose', 'close'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      // Templates cannot reach module scope, and the emitted value is a contract
      PRICE_CHOICE_AUTOLINE,
      PRICE_CHOICE_ACTION,
    }
  },
  computed: {
    clock() {
      return this.now || new Date()
    },
    action() {
      return actionPrice(this.accessory, this.clock)
    },
    autolinePrice() {
      return toNumber(this.accessory.Price?.[this.vat])
    },
    actionAtVat() {
      if (!this.action) return null
      return this.vat === VAT_EXCL ? this.action.excl : this.action.incl
    },
    intro() {
      return this.i18n.t('/AccessoryCatalog/PriceChoiceIntro', {
        name: displayTitle(this.accessory),
      })
    },
    priceSub() {
      const mounted = hasLabour(this.accessory)
      if (this.vat === VAT_EXCL)
        return mounted
          ? this.i18n.t('/AccessoryCatalog/MountedExclVat')
          : this.i18n.t('/AccessoryCatalog/ExclVat')
      return mounted
        ? this.i18n.t('/AccessoryCatalog/MountedInclVat')
        : this.i18n.t('/AccessoryCatalog/InclVat')
    },
    validitySub() {
      const validTo = this.accessory.PonAccessoryCatalog?.ActionValidTo
      if (!validTo) return this.i18n.t('/AccessoryCatalog/PriceChoiceActionSubOpen')
      return this.i18n.t('/AccessoryCatalog/PriceChoiceActionSub', {
        date: dateHelper.formatDate(validTo),
      })
    },
  },
  mounted() {
    // Capture, so Escape closes this dialog without also closing the catalog
    // overlay behind it. Modal.vue's own Escape handler is a bubble-phase
    // listener on document with no stopPropagation, so it would not give this
    // guarantee — the overlay listens on document too and both would fire.
    document.addEventListener('keydown', this.onKeydown, true)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown, true)
  },
  methods: {
    /** modalFocus: land on the leading price, which is the default answer. */
    modalFocusFirst() {
      return this.$refs.autoline
    },
    onKeydown(event) {
      if (event.key !== 'Escape') return
      event.stopImmediatePropagation()
      event.preventDefault()
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
.action-price-dialog {
  position: fixed;
  inset: 0;
  z-index: z-index(prompt);
  display: grid;
  place-items: center;
  padding: 20px;
  // Same shade as Modal.vue: the two stack, and two different blacks read as a
  // rendering fault rather than as depth.
  background: rgba(0, 0, 0, 0.5);

  &__box {
    width: min(540px, 100%);
    max-height: 100%;
    overflow-y: auto;
    background: $white;
    border-radius: $border-radius-md;
    box-shadow: $shadow-lg;
    padding: 22px 24px 18px;

    h3 {
      // Size and weight come from the global heading scale (app/_css-vars.scss),
      // like Modal.vue's own title.
      margin: 0 0 6px;
      color: var(--color-primary);
    }
  }

  &__intro {
    margin: 0 0 16px;
    font-size: 13.5px;
    color: $gray-darker;
    line-height: 1.5;
  }

  &__choices {
    display: grid;
    gap: 10px;
  }

  &__choice {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 14px 16px;
    border: 1.5px solid $gray-light;
    border-radius: $border-radius-md;
    background: $white;
    text-align: left;
    font-family: inherit;
    color: $text-color;
    cursor: pointer;
    transition:
      border-color 0.15s,
      background 0.15s;

    // The app sets a global `*:focus { outline: 1px dotted }` in styles/app/_accessibility.scss,
    // which paints on mouse click too. Suppress it here so only keyboard focus is indicated.
    &:focus {
      outline: none;
    }

    &:hover,
    &:focus-visible {
      border-color: var(--color-primary);
      background: var(--color-primary-light);
      outline: none;
    }

    .k {
      font-size: 14px;
      font-weight: 700;
    }

    .v {
      text-align: right;
      font-size: 1.05em;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    small {
      display: block;
      margin-top: 2px;
      font-size: 11.5px;
      font-weight: 400;
      color: $gray-dark;
    }

    &--action {
      border-color: rgba($color-success-dark, 0.5);
      color: $color-success-dark;

      // The app sets a global `*:focus { outline: 1px dotted }` in styles/app/_accessibility.scss,
      // which paints on mouse click too. Suppress it here so only keyboard focus is indicated.
      &:focus {
        outline: none;
      }

      &:hover,
      &:focus-visible {
        border-color: $color-success-dark;
        background: $color-success-light;
      }
    }
  }

  &__foot {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
</style>
